import { useState, useEffect, useRef } from "react";
import { useColors } from "../ColorsContext";
import ProgressDots from "../components/ProgressDots";
import WristbandCTA from "../components/WristbandCTA";

const ALL_ALLIES = [
  { name: "Reyes", role: "Scout",     emoji: "🧭" },
  { name: "Mira",  role: "Medic",     emoji: "⚕️" },
  { name: "Tomas", role: "Engineer",  emoji: "🔧" },
  { name: "Jin",   role: "Hacker",    emoji: "💻" },
  { name: "Dex",   role: "Pilot",     emoji: "✈️" },
  { name: "Atlas", role: "Commander", emoji: "🎖️" },
];

function fmt(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

export default function ScreenCheckpointAction({
  path, checkpoint, checkpointIndex, totalCheckpoints, allyResults, onComplete, onBack, layout,
}) {
  const C = useColors();
  const { scale = 1, offsetX = 0, vpWidth = 390 } = layout || {};
  const extraLeft = offsetX / scale;
  const edgeStyle = {
    marginLeft: -extraLeft, width: vpWidth / scale,
    paddingLeft: extraLeft + 14, paddingRight: extraLeft + 14,
  };
  const [phase, setPhase] = useState("ready");
  const [timeLeft, setTimeLeft] = useState(checkpoint.actionTime);
  const [elapsed, setElapsed] = useState(0);
  const [saved, setSaved] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [mode, setMode] = useState("physical");
  const [repCount, setRepCount] = useState("");
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => { return () => clearInterval(intervalRef.current); }, []);

  function startTimer() {
    setPhase("running");
    const start = Date.now();
    intervalRef.current = setInterval(() => {
      const gone = Math.floor((Date.now() - start) / 1000);
      const left = checkpoint.actionTime - gone;
      setElapsed(gone);
      setTimeLeft(Math.max(0, left));
      if (left <= 0) {
        clearInterval(intervalRef.current);
        resolveCheckpoint(gone, false);
      }
    }, 200);
  }

  function completeEarly() {
    clearInterval(intervalRef.current);
    const gone = checkpoint.actionTime - timeLeft;
    resolveCheckpoint(gone, true);
  }

  function resolveCheckpoint(time, beatIt) {
    setSaved(beatIt);
    setElapsed(time);
    setPhase("result");
  }

  function handleScan() {
    setScanning(true);
    setTimeout(() => onComplete({ xp: checkpoint.xp, splitTime: elapsed, allySaved: saved }), 1200);
  }

  const isRed = timeLeft <= 20 && phase === "running";
  const timerColor = isRed ? C.red : C.orange;
  const canScan = phase === "result";

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", background: C.navy, position: "relative" }}>

      {/* Top bar */}
      <div style={{
        background: C.navyMid, paddingTop: 10, paddingBottom: 10,
        display: "flex", alignItems: "center", gap: 8,
        borderBottom: `1px solid ${C.dim}`, flexShrink: 0,
        ...edgeStyle,
      }}>
        <button
          onClick={() => setShowLeaveModal(true)}
          style={{
            background: "transparent", border: "none",
            color: C.muted, fontSize: 20, cursor: "pointer",
            padding: "4px 6px", lineHeight: 1, flexShrink: 0,
          }}
        >←</button>
        <div style={{ fontSize: 11, color: path.color, fontWeight: 700, letterSpacing: 1, flex: 1 }}>
          {path.label.toUpperCase()} · CP {checkpointIndex + 1}/{totalCheckpoints}
        </div>
        <ProgressDots total={totalCheckpoints} current={checkpointIndex} />
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: 16 }}>
        {/* Participation toggle */}
        <div style={{
          display: "flex", gap: 6, marginBottom: 12,
          background: C.card, borderRadius: 8, padding: 4,
          border: `1px solid ${C.dim}`,
        }}>
          {[["physical", "🏃 Physical"], ["alternative", "♿ Alternative"]].map(([m, label]) => (
            <button key={m} onClick={() => setMode(m)} style={{
              flex: 1, padding: "8px 0", borderRadius: 6, border: "none",
              background: mode === m ? path.color : "transparent",
              color: mode === m ? "#fff" : C.muted,
              fontWeight: 600, fontSize: 12, cursor: "pointer",
            }}>{label}</button>
          ))}
        </div>

        {/* Obstacle header */}
        <div style={{
          background: C.card, borderRadius: 10, padding: "12px 14px",
          border: `1px solid ${C.dim}`, marginBottom: 12,
          display: "flex", gap: 12, alignItems: "center",
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: C.white, fontSize: 14 }}>{checkpoint.obstacle}</div>
            <div style={{ fontSize: 11, color: C.muted }}>{checkpoint.type} · Target: {fmt(checkpoint.actionTime)}</div>
          </div>
          <div style={{
            background: `${path.color}22`, color: path.color,
            borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 700,
          }}>+{checkpoint.xp} XP</div>
        </div>

        {/* Ally at stake */}
        <div style={{
          background: `${C.red}10`, borderRadius: 10, padding: 12,
          border: `1px solid ${C.red}44`, marginBottom: 12,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <div style={{
              width: 34, height: 34, borderRadius: "50%",
              background: `${C.orange}22`, border: `2px solid ${C.orange}`,
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18,
            }}>{checkpoint.ally.emoji}</div>
            <div>
              <div style={{ fontWeight: 700, color: C.white, fontSize: 13 }}>
                {checkpoint.ally.name} · {checkpoint.ally.role}
              </div>
              <div style={{ fontSize: 11, color: C.red }}>AT RISK</div>
            </div>
          </div>
          <div style={{ fontSize: 12, color: C.light, lineHeight: 1.6, fontStyle: "italic" }}>
            "{checkpoint.allyContext}"
          </div>
        </div>

        {/* Squad roster */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, color: C.muted, letterSpacing: 1, marginBottom: 8 }}>SQUAD STATUS</div>
          <div style={{ display: "flex", gap: 6 }}>
            {ALL_ALLIES.map((ally, i) => {
              const isPast = i < checkpointIndex;
              const isCurrent = i === checkpointIndex;
              const pastResult = isPast ? allyResults[i] : null;
              return (
                <div key={ally.name} style={{ flex: 1, textAlign: "center", opacity: isCurrent ? 1 : isPast ? 0.9 : 0.35 }}>
                  <div style={{
                    width: "100%", aspectRatio: "1", borderRadius: "50%",
                    background: isCurrent ? `${C.orange}33` : isPast && pastResult ? `${C.teal}22` : isPast ? `${C.red}22` : C.card,
                    border: `2px solid ${isCurrent ? C.orange : isPast && pastResult ? C.teal : isPast ? C.red : C.dim}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 24, marginBottom: 3,
                  }}>
                    {isPast && pastResult ? "✓" : isPast && !pastResult ? "✗" : ally.emoji}
                  </div>
                  <div style={{ fontSize: 14, color: C.muted }}>{ally.name}</div>
                </div>
              );
            })}
          </div>
        </div>

        {mode === "alternative" && phase !== "result" && (
          <div style={{
            background: `${C.gold}15`, borderRadius: 8, padding: 10,
            border: `1px solid ${C.gold}44`, marginBottom: 12,
          }}>
            <div style={{ fontSize: 11, color: C.gold, fontWeight: 700, marginBottom: 6 }}>♿ Alternative Role — Rep Tracker</div>
            <div style={{ fontSize: 12, color: C.light, marginBottom: 8, lineHeight: 1.5 }}>
              Count and record how many reps your teammate completes during the obstacle.
            </div>
            <input
              value={repCount}
              onChange={e => setRepCount(e.target.value)}
              placeholder="Enter rep count..."
              type="number" min="0"
              aria-label="Enter rep count"
              style={{
                width: "100%", padding: "10px 12px", borderRadius: 6,
                background: C.card, border: `1px solid ${C.dim}`,
                color: C.white, fontSize: 13, outline: "none", boxSizing: "border-box",
              }}
            />
          </div>
        )}

        {/* Timer / result */}
        {phase === "ready" && (
          <div style={{ textAlign: "center", marginBottom: 12 }}>
            <div style={{ fontSize: 52, fontWeight: 900, color: C.orange, marginBottom: 6, letterSpacing: -2 }}>
              {fmt(checkpoint.actionTime)}
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>
              Target time to save {checkpoint.ally.name}
            </div>
            <button
              onClick={startTimer}
              style={{
                padding: "16px 48px", background: C.orange, color: "#fff",
                border: "none", borderRadius: 8, fontWeight: 800, fontSize: 16,
                cursor: "pointer", boxShadow: `0 0 20px ${C.orange}60`, minHeight: 52,
              }}
            >START →</button>
          </div>
        )}

        {phase === "running" && (
          <div style={{ textAlign: "center", marginBottom: 12 }}>
            <div style={{ fontSize: 56, fontWeight: 900, color: timerColor, letterSpacing: -2, marginBottom: 4 }}>
              {fmt(timeLeft)}
            </div>
            {isRed && (
              <div style={{ fontSize: 12, color: C.red, fontWeight: 700, marginBottom: 12 }}>🔊 ⚠️ RUNNING OUT OF TIME!</div>
            )}
            {!isRed && (
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 12 }}>{checkpoint.ally.name} is waiting...</div>
            )}
            <button
              onClick={completeEarly}
              style={{
                padding: "14px 36px", background: C.teal, color: "#fff",
                border: "none", borderRadius: 8, fontWeight: 800, fontSize: 14,
                cursor: "pointer", minHeight: 52,
              }}
            >✓ OBSTACLE COMPLETE</button>
          </div>
        )}

        {phase === "result" && (
          <div style={{
            background: saved ? `${C.teal}18` : `${C.red}18`,
            borderRadius: 10, padding: 16,
            border: `1px solid ${saved ? C.teal : C.red}55`,
            marginBottom: 12, textAlign: "center",
          }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>{saved ? checkpoint.ally.emoji : "💔"}</div>
            <div style={{ fontWeight: 800, color: saved ? C.teal : C.red, fontSize: 17, marginBottom: 8 }}>
              🔊 {saved ? `${checkpoint.ally.name} is safe!` : `${checkpoint.ally.name} didn't make it.`}
            </div>
            <div style={{ fontSize: 13, color: C.light, lineHeight: 1.6 }}>
              {saved
                ? `You cleared ${checkpoint.obstacle} in ${fmt(elapsed)}. ${checkpoint.ally.name} made it out.`
                : `Time expired. ${checkpoint.ally.name} didn't make it. Come back next time.`}
            </div>
            {saved && (
              <div style={{
                marginTop: 10, background: `${C.teal}22`, borderRadius: 6,
                padding: "6px 12px", display: "inline-block",
                color: C.teal, fontSize: 12, fontWeight: 700,
              }}>+{checkpoint.xp} XP · Split: {fmt(elapsed)}</div>
            )}
          </div>
        )}
      </div>

      {/* Sticky wristband CTA */}
      <WristbandCTA
        canScan={canScan}
        outOfTries={false}
        pathColor={path.color}
        scanning={scanning}
        onScan={handleScan}
        layout={layout}
      />

      {/* Leave modal */}
      {showLeaveModal && (
        <div style={{
          position: "absolute", inset: 0, background: "rgba(0,0,0,0.72)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 100, padding: 24,
        }}>
          <div style={{
            background: C.card, borderRadius: 14, padding: 24,
            border: `1px solid ${C.dim}`, width: "100%",
          }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.white, marginBottom: 8 }}>
              Leave this checkpoint?
            </div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 20, lineHeight: 1.5 }}>
              Your timer progress will be lost.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => setShowLeaveModal(false)}
                style={{
                  flex: 1, padding: "12px 0", background: "transparent",
                  border: `1px solid ${C.dim}`, borderRadius: 8,
                  color: C.muted, fontSize: 13, cursor: "pointer",
                }}
              >Cancel</button>
              <button
                onClick={() => { clearInterval(intervalRef.current); setShowLeaveModal(false); onBack(); }}
                style={{
                  flex: 1, padding: "12px 0", background: C.red,
                  border: "none", borderRadius: 8,
                  color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer",
                }}
              >Leave</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
